import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import PageContainer from '../components/layout/PageContainer';
import { getFirestore, doc, updateDoc, getDoc } from 'firebase/firestore';
import { app } from '../firebaseConfig';
import InputMask from 'react-input-mask';
import { Listbox } from '@headlessui/react';
import { ArrowLeftIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

const genderOptions = [
  { value: '', label: 'Selecione' },
  { value: 'male', label: 'Homem' },
  { value: 'female', label: 'Mulher' },
];

// Add this type definition
const AnimatePresenceWrapper = AnimatePresence as React.ComponentType<{
  children: React.ReactNode;
  mode?: "wait" | "sync" | "popLayout";
}>;

const EditProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    birthDate: '',
    cpf: '',
    gender: '',
  });
  const [loading, setLoading] = useState(true);
  const [cpfError, setCpfError] = useState('');
  const [popupState, setPopupState] = useState({ visible: false, message: '', type: 'success' });
  const [birthDateError, setBirthDateError] = useState('');

  // Add date format conversion functions
  const formatDateForDisplay = (dateStr: string) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  const formatDateForStorage = (dateStr: string) => {
    if (!dateStr) return '';
    const [day, month, year] = dateStr.split('/');
    return `${year}-${month}-${day}`;
  };

  // Add date validation function
  const isValidDate = (dateStr: string) => {
    if (!dateStr) return false;
    
    // Check if the date string matches the format DD/MM/YYYY
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return false;
    
    const [day, month, year] = dateStr.split('/').map(Number);
    
    // Check if the date is valid
    const date = new Date(year, month - 1, day);
    return date.getDate() === day && 
           date.getMonth() === month - 1 && 
           date.getFullYear() === year &&
           year >= 1900 && 
           year <= new Date().getFullYear();
  };

  // Função de validação de CPF (reutilizada do OnboardingPage)
  function isValidCPF(cpf: string) {
    cpf = cpf.replace(/[^\\d]+/g, '');
    if (cpf.length !== 11 || /^(\\d)\\1+$/.test(cpf)) return false;
    let sum = 0, rest;
    for (let i = 1; i <= 9; i++) sum += parseInt(cpf.substring(i - 1, i)) * (11 - i);
    rest = (sum * 10) % 11;
    if (rest === 10 || rest === 11) rest = 0;
    if (rest !== parseInt(cpf.substring(9, 10))) return false;
    sum = 0;
    for (let i = 1; i <= 10; i++) sum += parseInt(cpf.substring(i - 1, i)) * (12 - i);
    rest = (sum * 10) % 11;
    if (rest === 10 || rest === 11) rest = 0;
    if (rest !== parseInt(cpf.substring(10, 11))) return false;
    return true;
  }

  // Buscar dados do usuário para pré-popular o formulário
  useEffect(() => {
    const fetchUserData = async () => {
      if (user?.uid) {
        const db = getFirestore(app);
        const userRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const data = userSnap.data();
          setFormData({
            name: data.name || '',
            phone: data.phone || '',
            birthDate: formatDateForDisplay(data.birthDate || ''),
            cpf: data.cpf || '',
            gender: data.gender || '',
          });
        }
      }
      setLoading(false);
    };
    fetchUserData();
  }, [user]);

  const handleCPFBlur = () => {
    if (formData.cpf && !isValidCPF(formData.cpf)) {
      setCpfError('CPF inválido');
    } else {
      setCpfError('');
    }
  };

  const handleBirthDateBlur = () => {
    if (formData.birthDate && !isValidDate(formData.birthDate)) {
      setBirthDateError('Data inválida');
    } else {
      setBirthDateError('');
    }
  };

  // Função para mostrar o pop-up
  const showPopup = (message: string, type: 'success' | 'error') => {
    setPopupState({ visible: true, message, type });
    setTimeout(() => {
      setPopupState({ ...popupState, visible: false });
    }, 3000); // Pop-up visível por 3 segundos
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (cpfError || birthDateError) return; // Don't submit if there are any errors
    
    if (user) {
      const db = getFirestore(app);
      const userRef = doc(db, 'users', user.uid);
      const cleanCPF = formData.cpf.replace(/\D/g, '');
      const cleanPhone = formData.phone.replace(/\D/g, '');
      
      try {
        await updateDoc(userRef, {
          name: formData.name,
          phone: cleanPhone,
          birthDate: formatDateForStorage(formData.birthDate),
          cpf: cleanCPF,
          gender: formData.gender,
        });
        showPopup('Perfil atualizado com sucesso!', 'success');
        setTimeout(() => {
          navigate('/profile');
        }, 1500);
      } catch (error) {
        console.error('Erro ao atualizar perfil:', error);
        showPopup('Erro ao atualizar perfil. Tente novamente.', 'error');
      }
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="flex justify-center items-center min-h-[calc(100vh-64px)]">
          <span className="text-white">Carregando...</span>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
        <div className="max-w-md mx-auto w-full flex flex-col justify-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/10 backdrop-blur-lg rounded-xl p-6"
          >
            <div className="flex items-center mb-6">
              <button onClick={() => navigate('/profile')} className="text-white/60 hover:text-white transition-colors mr-4">
                <ArrowLeftIcon className="h-6 w-6" />
              </button>
              <h1 className="text-2xl font-bold text-white">Editar Perfil</h1>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-white/60 mb-1">
                  Nome completo
                </label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-white/60 mb-1">
                  Telefone
                </label>
                <InputMask
                  mask="(99) 99999-9999"
                  value={formData.phone || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, phone: e.target.value || '' })}
                >
                  {(inputProps: any) => (
                    <input
                      {...inputProps}
                      type="tel"
                      id="phone"
                      className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="(99) 99999-9999"
                      required
                    />
                  )}
                </InputMask>
              </div>
              <div>
                <label htmlFor="cpf" className="block text-sm font-medium text-white/60 mb-1">
                  CPF
                </label>
                <InputMask
                  mask="999.999.999-99"
                  value={formData.cpf || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, cpf: e.target.value || '' })}
                  onBlur={handleCPFBlur}
                >
                  {(inputProps: any) => (
                    <input
                      {...inputProps}
                      type="text"
                      id="cpf"
                      className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="000.000.000-00"
                      required
                    />
                  )}
                </InputMask>
                {cpfError && <span className="text-red-400 text-sm">{cpfError}</span>}
              </div>
              <div>
                <label htmlFor="gender" className="block text-sm font-medium text-white/60 mb-1">
                  Gênero
                </label>
                <Listbox value={formData.gender} onChange={value => setFormData({ ...formData, gender: value })}>
                  <div className="relative">
                    <Listbox.Button className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-left focus:outline-none focus:ring-2 focus:ring-primary">
                      {genderOptions.find(opt => opt.value === formData.gender)?.label || 'Selecione'}
                    </Listbox.Button>
                    <Listbox.Options className="absolute mt-1 w-full bg-zinc-900/90 text-white rounded-lg shadow-lg z-10">
                      {genderOptions.map(option => (
                        <Listbox.Option
                          key={option.value}
                          value={option.value}
                          className={({ active }) =>
                            `cursor-pointer select-none px-4 py-2 ${active ? 'bg-primary/60 text-white' : ''}`
                          }
                        >
                          {option.label}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </div>
                </Listbox>
              </div>
              <div>
                <label htmlFor="birthDate" className="block text-sm font-medium text-white/60 mb-1">
                  Data de nascimento
                </label>
                <InputMask
                  mask="99/99/9999"
                  value={formData.birthDate || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, birthDate: e.target.value || '' })}
                  onBlur={handleBirthDateBlur}
                >
                  {(inputProps: any) => (
                    <input
                      {...inputProps}
                      type="text"
                      id="birthDate"
                      className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="DD/MM/AAAA"
                      required
                    />
                  )}
                </InputMask>
                {birthDateError && <span className="text-red-400 text-sm">{birthDateError}</span>}
              </div>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                className="w-full bg-primary text-white py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors"
              >
                Salvar Alterações
              </motion.button>
            </form>
          </motion.div>
          {/* Pop-up de Status */}
          <AnimatePresenceWrapper>
            {popupState.visible && (
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 50 }}
                className={`absolute top-1/4 inset-x-0 mx-auto w-fit p-4 rounded-lg shadow-lg text-white flex items-center gap-2 ${
                  popupState.type === 'success' ? 'bg-green-500' : 'bg-red-500'
                }`}
              >
                {popupState.type === 'success' ? <CheckCircleIcon className="h-6 w-6" /> : <XCircleIcon className="h-6 w-6" />}
                <span>{popupState.message}</span>
              </motion.div>
            )}
          </AnimatePresenceWrapper>
        </div>
      </div>
    </PageContainer>
  );
};

export default EditProfilePage; 